/**
 * Regole pure della lista Instagram/Messenger (niente React ne' Supabase):
 * fissate in cima e archiviate a parte, con le stesse regole della lista
 * WhatsApp (chatListRules) e la chiave 'meta:<id>' di whatsapp_chat_meta.
 */
import { arrangeChats, isMuted, type ChatLike, type ChatListPrefs } from '@/lib/services/whatsapp/chatListRules';
import type { MetaConversation } from '@/lib/stores/metaMessagingStore';

/** Chiave della conversazione in whatsapp_chat_meta (note, stato, fissa/archivia). */
export function metaPrefsKey(conversationId: string): string {
  return `meta:${conversationId}`;
}

/**
 * Ultima attivita' del contatto: un'archiviata torna in lista solo se il
 * contatto scrive dopo l'archiviazione (un nostro messaggio non la riapre).
 */
export function metaChatLike(conversation: Pick<MetaConversation, 'id' | 'last_inbound_at'>): ChatLike {
  const ts = conversation.last_inbound_at ? new Date(conversation.last_inbound_at).getTime() : 0;
  return { id: conversation.id, lastMessage: Number.isFinite(ts) && ts > 0 ? { timestamp: ts, fromMe: false } : null };
}

/**
 * Divide le conversazioni (gia' ordinate per ultima attivita') in visibili, con
 * le fissate in cima, e archiviate.
 */
export function arrangeMetaConversations<T extends Pick<MetaConversation, 'id' | 'last_inbound_at'>>(
  conversations: T[],
  prefsOf: (key: string) => ChatListPrefs | null | undefined,
): { visible: T[]; archived: T[] } {
  const wrapped = conversations.map((c) => ({ ...metaChatLike(c), conversation: c }));
  const { visible, archived } = arrangeChats(wrapped, (id) => prefsOf(metaPrefsKey(id)));
  return { visible: visible.map((w) => w.conversation), archived: archived.map((w) => w.conversation) };
}

/**
 * Conversazioni da leggere per i badge globali (menu e icona Messaggi), senza
 * quelle silenziate: stessa regola dei non letti WhatsApp.
 */
export function metaUnreadExcludingMuted<T extends Pick<MetaConversation, 'id'>>(
  conversations: T[],
  isUnread: (c: T) => boolean,
  prefsOf: (key: string) => ChatListPrefs | null | undefined,
  now: number = Date.now(),
): number {
  let total = 0;
  for (const c of conversations) {
    if (isUnread(c) && !isMuted(prefsOf(metaPrefsKey(c.id)), now)) total++;
  }
  return total;
}
